import { useState } from 'react';
import type { Category } from '../../../mocks/categories';
import { openWhatsApp } from '../../../utils/whatsapp';
import { trackEvent } from '../../../utils/tracking';
import InlineCitySelector from './InlineCitySelector';

interface CategoryCardProps {
  category: Category;
  featured?: boolean;
}

export default function CategoryCard({ category, featured = false }: CategoryCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [activeTarget, setActiveTarget] = useState<string | null>(null);

  const openCitySelector = (targetName: string) => {
    setActiveTarget(targetName);
  };

  const closeCitySelector = () => {
    setActiveTarget(null);
  };

  const handleSend = (city: string) => {
    if (!activeTarget) return;
    trackEvent('category_click', { category: activeTarget, city, section: 'main_grid' });
    trackEvent('whatsapp_redirect', { category: activeTarget, city });
    openWhatsApp(activeTarget, city);
  };

  const handleMainClick = () => openCitySelector(category.name);

  const handleSubClick = (e: React.MouseEvent, subName: string) => {
    e.stopPropagation();
    openCitySelector(subName);
  };

  const handleExpandToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  return (
    <div
      className={`relative rounded-2xl overflow-hidden transition-all duration-300 group ${featured ? 'md:col-span-1' : ''}`}
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: `1px solid ${category.gradientFrom}30`,
        backdropFilter: 'blur(16px)',
      }}
    >
      {/* Hover glow */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"
        style={{
          background: `linear-gradient(135deg, ${category.gradientFrom}08, ${category.gradientTo}06)`,
          boxShadow: `inset 0 0 0 1px ${category.gradientFrom}50`,
        }}
      />

      {/* Hot badge */}
      {category.isHot && (
        <div
          className="absolute top-3 right-3 z-10 flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
          style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.4)', color: '#fca5a5' }}
        >
          <span>🔥</span> Popüler
        </div>
      )}

      {/* Card header */}
      <div
        className="p-5 flex flex-col gap-4 cursor-pointer"
        onClick={handleMainClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleMainClick()}
        aria-label={`${category.name} için şehir seç`}
      >
        <div className="flex items-start justify-between">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
            style={{
              background: `linear-gradient(135deg, ${category.gradientFrom}30, ${category.gradientTo}20)`,
              border: `1px solid ${category.gradientFrom}40`,
            }}
          >
            <i className={`${category.icon} text-2xl`} style={{ color: category.gradientFrom }} />
          </div>
          <div className="flex items-center gap-1 text-gray-600 text-xs">
            <i className="ri-group-line text-xs" />
            {(category.clickCount ?? 0).toLocaleString('tr-TR')}
          </div>
        </div>

        <div>
          <h3
            className="text-white font-bold text-base mb-1 group-hover:text-purple-200 transition-colors duration-300"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            {category.name}
          </h3>
          <p className="text-gray-500 text-xs leading-relaxed">{category.description}</p>
        </div>

        <div
          className="flex items-center justify-between pt-3 border-t"
          style={{ borderColor: 'rgba(255,255,255,0.06)' }}
        >
          <span className="flex items-center gap-1.5 text-xs font-medium" style={{ color: category.gradientFrom }}>
            <i className="ri-map-pin-line text-base" />
            Şehir Seç &amp; Kampanya Al
          </span>
          <span className="text-gray-600 text-xs">{category.subcategories.length} alt kategori</span>
        </div>
      </div>

      {/* Inline city selector */}
      {activeTarget !== null && (
        <InlineCitySelector
          accentColor={category.gradientFrom}
          targetName={activeTarget}
          onClose={closeCitySelector}
          onSend={handleSend}
        />
      )}

      {/* Expand toggle */}
      <button
        onClick={handleExpandToggle}
        className="w-full px-5 pb-3 flex items-center gap-2 text-gray-500 hover:text-gray-300 text-xs transition-colors duration-200 cursor-pointer whitespace-nowrap"
        aria-expanded={expanded}
      >
        <i className={`ri-arrow-${expanded ? 'up' : 'down'}-s-line text-sm transition-transform duration-300`} />
        {expanded ? 'Alt Kategorileri Kapat' : 'Alt Kategorileri Gör'}
      </button>

      {/* Subcategory pills */}
      {expanded && (
        <div
          className="px-5 pb-5 flex flex-wrap gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {category.subcategories.map((sub) => (
            <button
              key={sub.id}
              onClick={(e) => handleSubClick(e, sub.name)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105 ${
                activeTarget === sub.name ? 'ring-1' : ''
              }`}
              style={{
                background: activeTarget === sub.name ? `${category.gradientFrom}30` : `${category.gradientFrom}15`,
                border: `1px solid ${category.gradientFrom}${activeTarget === sub.name ? '80' : '35'}`,
                color: category.gradientFrom,
              }}
            >
              <i className="ri-map-pin-line text-xs" />
              {sub.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
